import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cdnUrl } from "commons/cdn";
import { Plus, X } from "lucide-react";
import {
  SpineTemplate,
  isChildSpineTemplate,
} from "../../../types/spine-template";
import { getTemplateStateInfo } from "../../../types/templateState";
import { Dot } from "components/Dot";
import { MyImage } from "components/MyImage";

interface TemplateItemProps {
  template: Partial<SpineTemplate>;
  onSelect?: () => void;
  onRemove?: () => void;
  showAction?: boolean;
  isSelected?: boolean;
}

export function TemplateItem({
  template,
  onSelect,
  onRemove,
  showAction = true,
  isSelected = false,
}: TemplateItemProps) {
  const getTypeLabel = (type?: string) => {
    if (!type) return "-";
    return type;
  };

  return (
    <div
      className={`
        flex flex-wrap items-start gap-3 p-3 rounded-lg border transition-colors
        ${isSelected ? "bg-accent border-primary" : "bg-card border-border hover:bg-accent/50"}
      `}
    >
      {/* 图片区域 */}
      <div className="flex gap-2 shrink-0">
        {/* Cover 区域 */}
        {template.initDataId ? (
          template.cover ? (
            <MyImage
              src={cdnUrl(template.cover)}
              alt="Cover"
              className="aspect-square rounded-md object-cover w-16 h-16 border border-border"
            />
          ) : (
            <div className="aspect-square rounded-md bg-muted w-16 h-16 flex items-center justify-center text-[10px] text-muted-foreground border border-border leading-tight text-center px-1">
              无 Cover Url
            </div>
          )
        ) : (
          <div className="aspect-square rounded-md bg-muted w-16 h-16 flex items-center justify-center text-[10px] text-muted-foreground border border-border">
            无 Cover
          </div>
        )}

        {/* Preview 区域 */}
        {template.preview ? (
          <MyImage
            src={cdnUrl(template.preview)}
            alt="Preview"
            className="aspect-square rounded-md object-cover w-16 h-16 border border-border"
          />
        ) : (
          <div className="aspect-square rounded-md bg-muted w-16 h-16 flex items-center justify-center text-[10px] text-muted-foreground border border-border">
            无 Preview
          </div>
        )}
      </div>

      {/* 信息区域 */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-medium text-sm break-all">
            {template.name || template.id}
          </span>
          {(() => {
            const stateInfo = getTemplateStateInfo(template.state);
            if (stateInfo) {
              return (
                <div className="flex items-center gap-1">
                  <Dot className={stateInfo.className} />
                  <span className="text-[10px]">{stateInfo.label}</span>
                </div>
              );
            }
            return null;
          })()}
          <Badge variant="secondary" className="text-[10px] px-1.5 h-4">
            {getTypeLabel(template.type)}
          </Badge>
          {isChildSpineTemplate(template) && (
            <Badge variant="outline" className="text-[10px] px-1.5 h-4">
              子模版
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground font-mono break-all mb-1">
          {template.id}
        </div>
        {isChildSpineTemplate(template) && template.parentId && (
          <div className="text-[11px] text-muted-foreground mb-1">
            父模版: {template.parentId}
          </div>
        )}
        {template.intro && (
          <div className="text-xs text-muted-foreground break-words">
            {template.intro}
          </div>
        )}
      </div>

      {/* 操作按钮 */}
      {showAction && (
        <div className="shrink-0">
          {onRemove ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onRemove}
            >
              <X className="h-4 w-4" />
            </Button>
          ) : onSelect ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onSelect}
              disabled={isSelected}
            >
              <Plus className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
